import { FormDataAPIType } from 'components/common/form';
import React, { useRef, useEffect, useMemo, useState } from 'react';
import { TemplateFormComponent } from 'components/common/form/layout/ReactHookFormController';
import BlockEditPanelControls from 'app/editor/report/content-editor/block-edit-panel/BlockEditPanelControls';
import EditPanelCheckBox from 'app/editor/report/content-editor/block-edit-panel/edit-panel-components/EditPanelCheckBox';
import useBundleTranslation from 'i18n';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import { Box, FormLabel, Stack } from '@mui/material';
import BlockEditPanelHeader from 'app/editor/report/content-editor/block-edit-panel/BlockEditPanelHeader';
import {
    BlockComponent,
    ComponentSettingsManageFilters,
    ContentSettings,
    DatasetFieldValueType,
    RendererFilter,
    RendererFilterType,
} from '@report-content';
import { useQuery } from '@tanstack/react-query';
import { instance } from 'api/api';
import { FormComponentValue } from 'components/common/form/layout/control';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { AssocArray, YNValues } from 'tools/types';
import FieldDefaultsControl from './FieldDefaultsControl';

interface ISortOrderDirections {
    order: 'ASC' | 'DESC';
    label: string;
    valueType: DatasetFieldValueType;
}

export const sortOrderDirections: Array<ISortOrderDirections> = [
    { order: 'ASC', label: 'label_a_to_z', valueType: 'text' },
    { order: 'DESC', label: 'label_z_to_a', valueType: 'text' },
    { order: 'ASC', label: 'label_ascending', valueType: 'numeric' },
    { order: 'DESC', label: 'label_descending', valueType: 'numeric' },
    { order: 'ASC', label: 'label_ascending', valueType: 'datetime' },
    { order: 'DESC', label: 'label_descending', valueType: 'datetime' },
];

interface IFilterType {
    type: RendererFilterType;
    label: string;
    valueType: DatasetFieldValueType;
}
const filterTypes: Array<IFilterType> = [
    { type: 'dropdown', label: 'label_dropdown', valueType: 'text' },
    { type: 'multiselect', label: 'label_multiselect', valueType: 'text' },
    { type: 'dropdown', label: 'label_dropdown', valueType: 'numeric' },
    { type: 'multiselect', label: 'label_multiselect', valueType: 'numeric' },
    { type: 'range', label: 'label_range', valueType: 'numeric' },
    { type: 'dropdown', label: 'label_dropdown', valueType: 'datetime' },
    { type: 'multiselect', label: 'label_multiselect', valueType: 'datetime' },
    { type: 'range', label: 'label_range', valueType: 'datetime' },
    { type: 'charting_interval', label: 'label_charting_interval', valueType: 'datetime' },
];

export default function FieldForm({
    field,
    onApply,
    onCancel,
    title,
    elementId,
    contentSettings,
    component,
    onlyDisplayRelevantValues,
}: {
    field: RendererFilter;
    onApply: (field: RendererFilter) => void;
    onCancel: () => void;
    title?: string;
    elementId: number;
    contentSettings: ContentSettings;
    component: BlockComponent<ComponentSettingsManageFilters>;
    onlyDisplayRelevantValues?: YNValues;
}) {
    const { t } = useBundleTranslation(['components/report-content']);
    const elementProps: FormDataAPIType = useCustomSimplifiedForm({
        filter_label: field.filter_label,
        include_all_value: field.include_all_value ?? 'Y',
        show_date_only: field.show_date_only ?? 'N',
        filterType: field.filterType ?? 'dropdown',
        sortDirection: field.sortDirection,
        use_external_filter_value: field.use_external_filter_value ?? 'N',
        external_dataset_id: field.external_dataset_id ? String(field.external_dataset_id) : '',
        external_dataset_column_value_id: field.external_dataset_column_value_id ?? '',
        external_dataset_column_value_label: field.external_dataset_column_value_label ?? '',
        external_dataset_column_title_id: field.external_dataset_column_title_id ?? '',
        external_dataset_column_title_label: field.external_dataset_column_title_label ?? '',
        external_delimiter: field.external_delimiter ?? '~',
        set_default_value: field.set_default_value ?? 'N',
        filter_default_value: field.filter_default_value ?? [],
        override_all_value: field.override_all_value ?? 'N',
        override_all_value_label: field.override_all_value_label ?? 'All',
    });

    const fieldType: RendererFilterType = elementProps.form.hookFormWatch('filterType');
    const valueType = field.value_type;
    const forceIncludeAllValue = fieldType === 'dropdown' && onlyDisplayRelevantValues === 'Y';
    // Range and Charting Interval filters aren't supported by relevant-values narrowing.
    const rangeTypesDisabled = onlyDisplayRelevantValues === 'Y';

    useEffect(() => {
        if (forceIncludeAllValue) {
            elementProps.form.hookFormSetValue('include_all_value', 'Y');
        }
    }, [forceIncludeAllValue]);

    useEffect(() => {
        if (rangeTypesDisabled && (fieldType === 'range' || fieldType === 'charting_interval')) {
            elementProps.form.hookFormSetValue('filterType', 'dropdown');
        }
    }, [rangeTypesDisabled, fieldType]);

    const includeAllValue = elementProps.form.hookFormWatch('include_all_value');
    const overrideAllValue = elementProps.form.hookFormWatch('override_all_value');
    const useExternalFilterValue = elementProps.form.hookFormWatch('use_external_filter_value');
    const selectedDatasetId = elementProps.form.hookFormWatch('external_dataset_id');
    const setDefaultValue = elementProps.form.hookFormWatch('set_default_value');

    const { data: datasetsList = [], isLoading: isDatasetListLoading } = useQuery<FormComponentValue[]>({
        queryKey: ['manage-filters', 'external-datasets'],
        queryFn: async () => {
            const response = await instance.get(`data/editor/report/${elementId}/get-datasets`);
            return response.data?.data ?? [];
        },
        enabled: useExternalFilterValue === 'Y',
    });

    const selectedDataset = useMemo(() => {
        if (!datasetsList || !selectedDatasetId) return null;
        return datasetsList.find((d) => d.value == selectedDatasetId) || null;
    }, [selectedDatasetId, datasetsList]);

    const { data: columnsList = [], isLoading: isColumnsListLoading } = useQuery<FormComponentValue[]>({
        queryKey: ['manage-filters', 'dataset-columns', selectedDatasetId],
        queryFn: async () => {
            let result: AssocArray<string> = {};
            if (selectedDataset?.props?.can_view == 'Y') {
                const response = await instance.get(`/data/editor/dataset/${selectedDatasetId}/columns`);
                result = response.data ?? [];
            } else {
                if (field.external_dataset_column_value_id) {
                    result[field.external_dataset_column_value_id] = field.external_dataset_column_value_label ?? '';
                }
                if (field.external_dataset_column_title_id) {
                    result[field.external_dataset_column_title_id] = field.external_dataset_column_title_label ?? '';
                }
            }

            return [{ value: '', label: '--' }].concat(
                Object.entries(result).map(([value, label]): FormComponentValue => ({
                    value: String(value),
                    label: label,
                })),
            );
        },
        enabled: useExternalFilterValue === 'Y' && selectedDataset != null,
    });

    const prevDatasetIdRef = useRef(selectedDatasetId);
    useEffect(() => {
        if (prevDatasetIdRef.current !== selectedDatasetId) {
            prevDatasetIdRef.current = selectedDatasetId;
            elementProps.form.hookFormSetValue('external_dataset_column_value_id', '');
            elementProps.form.hookFormSetValue('external_dataset_column_title_id', '');
            elementProps.form.hookFormSetValue('external_dataset_column_value_label', '');
            elementProps.form.hookFormSetValue('external_dataset_column_title_label', '');
        }
    }, [selectedDatasetId, elementProps.form]);

    const [formFields, setFormField] = useState({});
    useEffect(() => {
        const subscription = elementProps.form.hookFormWatch((value, { name, type }) => {
            const list = elementProps.form.hookFormGetValues();
            if (JSON.stringify(formFields) != JSON.stringify(list)) {
                setFormField(list);
            }
        });

        return () => subscription?.unsubscribe();
    }, [elementProps.form.hookFormWatch]);

    const currentFieldState = useMemo(() => {
        const titleId = elementProps.form.hookFormGetValues('external_dataset_column_title_id') ?? '';
        const valueId = elementProps.form.hookFormGetValues('external_dataset_column_value_id') ?? '';
        const effectiveIncludeAllValue =
            elementProps.form.hookFormGetValues('filterType') == 'dropdown'
                ? elementProps.form.hookFormGetValues('include_all_value')
                : 'N';
        return {
            ...field,
            filter_label: elementProps.form.hookFormGetValues('filter_label'),
            include_all_value: effectiveIncludeAllValue,
            show_date_only: elementProps.form.hookFormGetValues('show_date_only'),
            filterType: elementProps.form.hookFormGetValues('filterType'),
            sortDirection: elementProps.form.hookFormGetValues('sortDirection'),
            use_external_filter_value: elementProps.form.hookFormGetValues('use_external_filter_value'),
            external_dataset_id: elementProps.form.hookFormGetValues('external_dataset_id')
                ? Number(elementProps.form.hookFormGetValues('external_dataset_id'))
                : null,
            external_dataset_column_value_id: valueId,
            external_dataset_column_value_label: columnsList.find((v) => v.value == valueId)?.label ?? '',
            external_dataset_column_title_id: titleId,
            external_dataset_column_title_label: columnsList.find((v) => v.value == titleId)?.label ?? '',
            external_delimiter: elementProps.form.hookFormGetValues('external_delimiter') ?? null,
            set_default_value: elementProps.form.hookFormGetValues('set_default_value') ?? 'N',
            filter_default_value: elementProps.form.hookFormGetValues('filter_default_value') ?? [],
            override_all_value:
                effectiveIncludeAllValue === 'Y'
                    ? (elementProps.form.hookFormGetValues('override_all_value') ?? 'N')
                    : 'N',
            override_all_value_label: elementProps.form.hookFormGetValues('override_all_value_label') ?? 'All',
        };
    }, [field, columnsList, formFields]);

    return (
        <Stack style={{ position: 'relative' }} height={1}>
            <Box flexShrink={0}>
                <BlockEditPanelHeader
                    title={title}
                    onCancel={() => onCancel()}
                    item={field.column_name ?? field.reference_name}
                />
            </Box>
            <Stack sx={{ overflow: 'auto', flexGrow: 1, p: 3 }} spacing={2}>
                {/*ToDo Show labels as on design*/}
                <TemplateFormComponent
                    elementProps={elementProps}
                    component={'FormText'}
                    name={'filter_label'}
                    props={{ label: 'label' }}
                />

                <Box>
                    <FormLabel>{t('manage_filters.label_filter_type')}</FormLabel>
                    <TemplateFormComponent
                        elementProps={{ ...elementProps }}
                        componentValues={filterTypes
                            .filter((f) => f.valueType == valueType)
                            .filter(
                                (f) => !rangeTypesDisabled || (f.type !== 'range' && f.type !== 'charting_interval'),
                            )
                            .map((f) => ({ label: t('manage_filters.' + f.label), value: f.type }))}
                        component={'FormSelect'}
                        name={'filterType'}
                    />
                </Box>

                <span className={fieldType == 'dropdown' || fieldType == 'multiselect' ? '' : 'd-none'}>
                    <FormLabel>{t('sort')}</FormLabel>
                    <TemplateFormComponent
                        elementProps={elementProps}
                        componentValues={sortOrderDirections
                            .filter((f) => f.valueType == valueType)
                            .map((f) => ({ label: t('manage_filters.' + f.label), value: f.order }))}
                        component={'FormSelect'}
                        name={'sortDirection'}
                    />
                </span>

                <span className={fieldType == 'dropdown' ? '' : 'd-none'}>
                    <EditPanelCheckBox
                        elementProps={elementProps}
                        name={'include_all_value'}
                        label={t('manage_filters.include_all_value')}
                        disabled={forceIncludeAllValue}
                    />
                </span>

                {fieldType == 'dropdown' && includeAllValue === 'Y' && (
                    <EditPanelCheckBox
                        elementProps={elementProps}
                        name={'override_all_value'}
                        label={t('manage_filters.override_all_value')}
                    />
                )}

                {fieldType == 'dropdown' && includeAllValue === 'Y' && overrideAllValue === 'Y' && (
                    <TemplateFormComponent
                        elementProps={elementProps}
                        component={'FormText'}
                        name={'override_all_value_label'}
                        props={{ label: t('manage_filters.override_all_value_label') }}
                    />
                )}

                <span className={valueType == 'datetime' && fieldType == 'range' ? '' : 'd-none'}>
                    <EditPanelCheckBox
                        elementProps={elementProps}
                        name={'show_date_only'}
                        label={t('manage_filters.show_date_only')}
                    />
                </span>

                <EditPanelCheckBox
                    elementProps={elementProps}
                    name={'use_external_filter_value'}
                    label={t('manage_filters.use_external_filter_value')}
                />

                {useExternalFilterValue === 'Y' && (
                    <>
                        {(isDatasetListLoading || isColumnsListLoading) && (
                            <LoadingPlaceholder relativeToParent={true} />
                        )}
                        <Box>
                            <FormLabel>{t('manage_filters.external_dataset')}</FormLabel>
                            <TemplateFormComponent
                                elementProps={elementProps}
                                componentValues={datasetsList}
                                component={'FormSelect'}
                                name={'external_dataset_id'}
                            />
                        </Box>
                        {selectedDatasetId && (
                            <Stack direction={'row'} spacing={2}>
                                <Box flex={1}>
                                    <FormLabel>{t('manage_filters.value_column')}</FormLabel>
                                    <TemplateFormComponent
                                        elementProps={elementProps}
                                        componentValues={columnsList}
                                        component={'FormSelect'}
                                        name={'external_dataset_column_value_id'}
                                    />
                                </Box>
                                <Box flex={1}>
                                    <FormLabel>{t('manage_filters.title_column')}</FormLabel>
                                    <TemplateFormComponent
                                        elementProps={elementProps}
                                        componentValues={columnsList}
                                        component={'FormSelect'}
                                        name={'external_dataset_column_title_id'}
                                    />
                                </Box>
                            </Stack>
                        )}
                        <Box>
                            <TemplateFormComponent
                                elementProps={elementProps}
                                component={'FormText'}
                                name={'external_delimiter'}
                                label={t('manage_filters.external_delimiter')}
                                props={{}}
                            />
                        </Box>
                    </>
                )}

                <EditPanelCheckBox
                    elementProps={elementProps}
                    name={'set_default_value'}
                    label={t('manage_filters.set_default_value')}
                />
                {setDefaultValue == 'Y' && (
                    <FieldDefaultsControl
                        currentFieldState={currentFieldState}
                        component={component}
                        contentSettings={contentSettings}
                        onChange={(filter, value) => {
                            elementProps.form.hookFormSetValue(
                                'filter_default_value',
                                Array.isArray(value) ? value : [value],
                            );
                        }}
                    />
                )}
            </Stack>
            <Box flexShrink={0}>
                <BlockEditPanelControls
                    onApply={() => onApply(currentFieldState)}
                    onCancel={() => onCancel()}
                    isTypeSave
                />
            </Box>
        </Stack>
    );
}
